/**
 * SignaturePad Component - Digital signature capture
 */

import React, { useRef, useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './SignaturePad.css';

const SignaturePad = ({
  onSignatureChange,
  disabled = false,
  required = true,
  showInstructions = true,
  instructionText = null,
  showLegalNotice = false,
  legalNoticeText = null,
  documentTitle = null
}) => {
  const sigPadRef = useRef();
  const [isEmpty, setIsEmpty] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // Check if signature pad is empty on mount and ensure proper canvas setup
    if (sigPadRef.current) {
      setIsEmpty(sigPadRef.current.isEmpty());

      // Ensure canvas has proper dimensions to avoid offset issues
      const canvas = sigPadRef.current.getCanvas();
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      sigPadRef.current.clear(); // Clear after resizing to ensure proper state
    }
  }, []);

  const handleSignatureEnd = () => {
    if (sigPadRef.current) {
      const isEmpty = sigPadRef.current.isEmpty();
      setIsEmpty(isEmpty);
      
      if (!isEmpty) {
        const signatureData = sigPadRef.current.toDataURL();
        onSignatureChange(signatureData);
      } else {
        onSignatureChange(null);
      }
    }
  };

  const clearSignature = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange(null);
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  // Default instructional text
  const defaultInstructionText = documentTitle
    ? `By providing your digital signature below you agree to the terms and conditions of ${documentTitle}`
    : 'By providing your digital signature below you agree to the terms and conditions of this document';

  // Default legal notice text
  const defaultLegalNoticeText = 'Your digital signature will be timestamped and legally binding. This document will be securely stored and available for download.';

  return (
    <div className="signature-section-wrapper">
      {instructionText !== null && instructionText !== false && (
        <p className="signature-instruction-text">
          {instructionText || defaultInstructionText}
        </p>
      )}

      <div className="signature-pad-container">
        <div className="signature-header">
          <h3 className="signature-title">
            Digital Signature {required && <span className="signature-required">*</span>}
          </h3>
          <button
            className="signature-clear-button"
            onClick={clearSignature}
            disabled={disabled || isEmpty}
            type="button"
          >
            Clear
          </button>
        </div>

        {showInstructions && (
          <p className="signature-instructions">
            {disabled
              ? 'Signature capture disabled'
              : 'Please sign in the box below using your mouse, trackpad, or finger on touch devices.'
          }
          </p>
        )}

        <div className={`signature-canvas-container ${isFocused ? 'focused' : ''}`}>
          <SignatureCanvas
            ref={sigPadRef}
            canvasProps={{
              className: 'signature-canvas',
              tabIndex: disabled ? -1 : 0,
              'aria-label': 'Digital signature canvas',
              role: 'application',
              onFocus: handleFocus,
              onBlur: handleBlur
            }}
            onEnd={handleSignatureEnd}
            onBegin={() => {
              setIsFocused(true);
              if (sigPadRef.current) {
                sigPadRef.current.getCanvas().focus();
              }
            }}
            clearOnResize={false}
            penColor="black"
            backgroundColor="transparent"
            disabled={disabled}
          />
          {isEmpty && (
            <div className="signature-placeholder">
              Click and drag to sign
            </div>
          )}
        </div>

        {isEmpty && required && (
          <p className="signature-error">
            Signature is required to proceed
          </p>
        )}
      </div>

      {showLegalNotice && (
        <div className="signature-legal-notice-box">
          <p className="signature-legal-notice-text">
            <strong>Legal Notice:</strong> {legalNoticeText || defaultLegalNoticeText}
          </p>
        </div>
      )}
    </div>
  );
};

export default SignaturePad;
